import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { sortBy, toLower } from 'lodash-es'
import {
  Checkbox,
  CheckboxFilterTree,
  type CheckedStateRecord,
  type FilterItemProps
} from '@jana-technology/platform-ui'

import { IContinuousRoute, INetwork } from '$/types'
import { IFilterModalChildComponentProps } from 'app/layout/Navbar/Filters/FilterModal/types'
import {
  selectFilteredNetworkList,
  selectStagingProfile
} from 'app/layout/Navbar/Filters/globalFilterSelectors'
import {
  setEditInProgressSync,
  setStagingProfileContinousRoutesSync
} from 'app/layout/Navbar/Filters/globalFilterSlice'
import LoadingSpinner from 'common/components/LoadingSpinner'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { SearchInput } from 'common/components/SearchInput'
import StyledToggle from 'common/components/StyledToggle'

//#region helpers
const lineToId = (line: IContinuousRoute): string => line.id.toString()

const deriveTreeItemsCount = (treeItems: FilterItemProps[]) =>
  treeItems.reduce((accumulator, item) => {
    if (item.children) {
      accumulator += item.children.length
    }

    return accumulator
  }, 0)

const filterSelectedItems = (
  selectedLineIds: string[],
  nodes: FilterItemProps[]
) =>
  nodes
    .map((node) => ({
      ...node,
      children: node.children?.filter((childNode) =>
        selectedLineIds.includes(childNode.value)
      )
    }))
    .filter((node) => node.children?.length)

const deriveNodeValues = (treeItems: FilterItemProps[]) =>
  treeItems
    .flatMap((node) => node.children)
    .map((childNode) => childNode?.value)
    .filter((value): value is string => Boolean(value))

const deriveCheckedState = (
  treeItems: FilterItemProps[],
  selectedLineIds: string[]
): CheckedStateRecord => {
  const selectedLineIdSet = new Set(selectedLineIds)

  return treeItems.reduce<CheckedStateRecord>((checkedState, treeItem) => {
    const children = treeItem.children ?? []
    const selectedChildrenCount = children.filter((child) =>
      selectedLineIdSet.has(child.value)
    ).length

    let parentCheckedState: boolean | 'indeterminate' = false

    if (selectedChildrenCount === children.length && children.length > 0) {
      parentCheckedState = true
    } else if (selectedChildrenCount > 0) {
      parentCheckedState = 'indeterminate'
    }

    checkedState[treeItem.value] = parentCheckedState

    children.forEach((child) => {
      checkedState[child.value] = selectedLineIdSet.has(child.value)
    })

    return checkedState
  }, {})
}

const getSelectedLeafValues = (
  treeItems: FilterItemProps[],
  checkedState: CheckedStateRecord
) =>
  treeItems.flatMap((treeItem) =>
    (treeItem.children ?? [])
      .filter((child) => checkedState[child.value] === true)
      .map((child) => child.value)
  )
//#endregion

interface ILineSelectorProps extends IFilterModalChildComponentProps {
  isLoading: boolean
}

const LineSelector = ({
  isLoading,
  searchValue,
  setSearchValue,
  onlyShowSelected,
  setOnlyShowSelected
}: ILineSelectorProps) => {
  const dispatch = useDispatch()
  const stagingProfile = useSelector(selectStagingProfile)
  const filteredNetworks = useSelector(selectFilteredNetworkList)

  const { allTreeItems, allTreeItemsCount } = useMemo(() => {
    const _networkToTreeNode = (network: INetwork): FilterItemProps => {
      const value = `network-${network.id}`

      return {
        value,
        label: network.name,
        children: sortBy(
          network.continuousRoutes.map((line) => {
            return {
              value: lineToId(line),
              label: line.name
            }
          }),
          'label'
        )
      }
    }

    const allTreeItems = sortBy(filteredNetworks, 'name').map(
      _networkToTreeNode
    )

    return {
      allTreeItems,
      allTreeItemsCount: deriveTreeItemsCount(allTreeItems)
    }
  }, [filteredNetworks])

  const selectedLineIds = useMemo(
    () => stagingProfile?.continuousRoutes.map(lineToId) || [],
    [stagingProfile?.continuousRoutes]
  )

  const selectedCount = selectedLineIds.length

  const [displayedTreeItems, setDisplayedTreeItems] =
    useState<FilterItemProps[]>(allTreeItems)

  const [selectedTreeItems, setSelectedTreeItems] =
    useState<FilterItemProps[]>(allTreeItems)

  const displayedTreeItemsCount = useMemo(
    () => deriveTreeItemsCount(displayedTreeItems),
    [displayedTreeItems]
  )

  // update the state after memoization
  useEffect(() => {
    if (allTreeItemsCount) {
      setDisplayedTreeItems(allTreeItems)
    }
  }, [allTreeItemsCount, allTreeItems])

  // debounce searching since there can be tons of lines (consumers)
  useEffect(() => {
    if (!searchValue) {
      if (!onlyShowSelected) {
        setDisplayedTreeItems(allTreeItems)
      }
    } else {
      const _id = setTimeout(() => {
        const searchedDisplayItems = allTreeItems
          .map((lineNode) => ({
            ...lineNode,
            children: lineNode.children?.filter((childNode) =>
              childNode.label.toLowerCase().includes(searchValue.toLowerCase())
            )
          }))
          .filter((node) => node.children?.length)

        setDisplayedTreeItems(searchedDisplayItems)
      }, 333)

      return () => clearTimeout(_id)
    }
  }, [allTreeItems, searchValue, onlyShowSelected])

  // prefill UI with initial continuousRoutes and stop reading them from redux

  const handleSearchInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value)
    },
    [setSearchValue]
  )

  const treeData = onlyShowSelected ? selectedTreeItems : displayedTreeItems

  const checkedState = useMemo(
    () => deriveCheckedState(allTreeItems, selectedLineIds),
    [allTreeItems, selectedLineIds]
  )

  const updateStagingProfile = useCallback(
    (lineIds: string[]) => {
      dispatch(setEditInProgressSync(true))

      dispatch(
        setStagingProfileContinousRoutesSync(
          filteredNetworks
            .flatMap((network) => network.continuousRoutes)
            .filter((continuousRoute) =>
              lineIds.includes(String(continuousRoute.id))
            )
        )
      )
    },
    [dispatch, filteredNetworks]
  )

  const handleCheckboxChange = (checked: boolean | 'indeterminate'): void => {
    if (stagingProfile) {
      if (checked === true) {
        updateStagingProfile(
          Array.from(
            new Set(
              stagingProfile?.continuousRoutes
                .map((route) => String(route.id))
                .concat(deriveNodeValues(displayedTreeItems) ?? [])
            )
          )
        )
        setSelectedTreeItems(displayedTreeItems)
      } else {
        const nodeValues = deriveNodeValues(displayedTreeItems)

        updateStagingProfile(
          stagingProfile?.continuousRoutes
            .filter(
              (displayedValue) =>
                !nodeValues.includes(String(displayedValue.id))
            )
            .map((route) => String(route.id)) ?? []
        )
        setSelectedTreeItems([])
        setOnlyShowSelected(false)
      }
    }
  }

  const handleOnlyShowSelectedToggle = () => {
    setOnlyShowSelected((onlyShowSelected) => !onlyShowSelected)
    setSearchValue('')
    setSelectedTreeItems(filterSelectedItems(selectedLineIds, allTreeItems))

    const searchedDisplayItems = allTreeItems
      .map((lineNode) => ({
        ...lineNode,
        children: lineNode.children?.filter((childNode) =>
          stagingProfile?.continuousRoutes.some(
            (route) => toLower(route.name) === toLower(childNode.label)
          )
        )
      }))
      .filter((node) => node.children?.length)

    setDisplayedTreeItems(searchedDisplayItems)
  }

  const reduxNotReady = !stagingProfile
  const isTreeLoading = isLoading

  const displayedLineIds = deriveNodeValues(displayedTreeItems)
  const isAllSelected =
    displayedLineIds.length > 0 &&
    displayedLineIds.every((lineId) => selectedLineIds.includes(lineId))

  const checkboxDisabled = reduxNotReady || !displayedTreeItemsCount

  const selectAllLabel = `Select All${
    displayedTreeItemsCount === allTreeItemsCount ? '' : ' Searched'
  }`

  return (
    <div
      className={[
        'flex flex-col',
        'w-[23rem] px-6 pb-4 pt-2',
        'border-l-2',
        'border-gray-200 bg-gray-100'
      ].join(' ')}
    >
      <div className='flex items-center justify-between'>
        <div className=''>
          Results{' '}
          <span className='font-semibold'>{displayedTreeItemsCount}</span>
        </div>
        <div className='text-gray-700'>
          <label className='flex items-center' htmlFor='only-selected-toggle'>
            <span>Only show selected</span>
            <StyledToggle
              id='only-selected-toggle'
              disabled={isTreeLoading}
              checked={onlyShowSelected}
              onChange={handleOnlyShowSelectedToggle}
            />
          </label>
        </div>
      </div>
      <div
        className={['mb-2 mr-2', onlyShowSelected ? 'opacity-50' : ''].join(
          ' '
        )}
      >
        <SearchInput
          className='flex-1'
          disabled={isTreeLoading || onlyShowSelected}
          placeholder='Search'
          value={searchValue}
          onChange={handleSearchInputChange}
        />
      </div>
      <LoadingSpinner isLoading={isTreeLoading}>
        {!isTreeLoading && (
          <div className='flex h-full flex-col'>
            <div className='flex min-h-0 flex-1 overflow-hidden bg-white p-1'>
              <OverlayScrollbar>
                <div className='flex items-center px-1 py-2'>
                  <Checkbox
                    id='select-all-checkbox'
                    aria-label={selectAllLabel}
                    checked={isAllSelected}
                    disabled={checkboxDisabled}
                    onCheckedChange={handleCheckboxChange}
                  />
                  <label className='ml-2 text-sm' htmlFor='select-all-checkbox'>
                    {selectAllLabel}
                  </label>
                  <div className='flex-1' />
                  <span className='mr-1 text-gray-700'>{selectedCount}</span>
                </div>
                <hr className='m-1' />
                <div
                  className={onlyShowSelected ? 'pointer-events-none' : ''}
                  aria-disabled={onlyShowSelected}
                >
                  <CheckboxFilterTree
                    key={`${stagingProfile?.id ?? 'no-profile'}-${onlyShowSelected}`}
                    className='ml-1 text-sm'
                    filters={treeData}
                    fullFilterTree={allTreeItems}
                    defaultExpandedValues={
                      onlyShowSelected
                        ? treeData.map((treeItem) => treeItem.value)
                        : []
                    }
                    checkedState={checkedState}
                    onCheckedChange={(nextCheckedState) => {
                      if (!reduxNotReady && !onlyShowSelected) {
                        updateStagingProfile(
                          getSelectedLeafValues(allTreeItems, nextCheckedState)
                        )
                      }
                    }}
                  />
                </div>
              </OverlayScrollbar>
              {allTreeItemsCount === 0 && (
                <div className='mt-32 w-full text-center'>
                  No results available for the selection.
                </div>
              )}
            </div>
          </div>
        )}
      </LoadingSpinner>
    </div>
  )
}

export default LineSelector
